/**
 * TicketPurchaseCard Component
 *
 * Allows users to purchase jackpot tickets directly with tokens.
 * Price: 500 tokens per ticket (JACKPOT_TICKET_PRICE_TOKENS)
 */

import { memo, useState } from 'react';
import { Ticket, Minus, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { JACKPOT_TICKET_PRICE_TOKENS } from '../../types/jackpot';
import { publicAsset } from '../../utils/assetUrl';

interface TicketPurchaseCardProps {
  onPurchase: (quantity: number) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  maxQuantity?: number;
}

export const TicketPurchaseCard = memo(function TicketPurchaseCard({
  onPurchase,
  isLoading = false,
  error = null,
  maxQuantity = 100,
}: TicketPurchaseCardProps) {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);

  const totalCost = quantity * JACKPOT_TICKET_PRICE_TOKENS;

  const handleIncrement = () => {
    setQuantity((prev) => Math.min(prev + 1, maxQuantity));
  };

  const handleDecrement = () => {
    setQuantity((prev) => Math.max(prev - 1, 1));
  };

  const handlePurchase = async () => {
    if (isLoading) return;
    await onPurchase(quantity);
    // Reset quantity after successful purchase
    setQuantity(1);
  };

  return (
    <div className="p-4 rounded-lg bg-slate-800/50 border border-amber-500/30">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Ticket className="w-5 h-5 text-amber-400" />
        <h4 className="font-medium text-amber-400">{t('jackpot.buyTickets', 'Buy Tickets')}</h4>
      </div>

      {/* Price info */}
      <p className="text-sm text-slate-400 mb-4 inline-flex items-center gap-1">
        {t('jackpot.ticketPrice', {
          price: JACKPOT_TICKET_PRICE_TOKENS.toLocaleString(),
        })}
        <img
          src={publicAsset('token-coin-32.png')}
          alt=""
          className="h-4 w-4 inline"
          draggable={false}
        />
      </p>

      {/* Quantity selector */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={handleDecrement}
          disabled={quantity <= 1 || isLoading}
          className="w-10 h-10 flex items-center justify-center rounded-lg
                     bg-slate-700 hover:bg-slate-600 disabled:opacity-50
                     disabled:cursor-not-allowed transition-colors"
          aria-label={t('common.decrease', 'Decrease')}
        >
          <Minus className="w-5 h-5 text-slate-300" />
        </button>

        <div className="text-center min-w-[60px]">
          <span className="text-2xl font-bold text-white">{quantity}</span>
          <span className="block text-xs text-slate-400">
            {quantity === 1 ? t('jackpot.ticket', 'ticket') : t('jackpot.tickets', 'tickets')}
          </span>
        </div>

        <button
          onClick={handleIncrement}
          disabled={quantity >= maxQuantity || isLoading}
          className="w-10 h-10 flex items-center justify-center rounded-lg
                     bg-slate-700 hover:bg-slate-600 disabled:opacity-50
                     disabled:cursor-not-allowed transition-colors"
          aria-label={t('common.increase', 'Increase')}
        >
          <Plus className="w-5 h-5 text-slate-300" />
        </button>
      </div>

      {/* Quick select buttons */}
      <div className="flex gap-2 mb-4 justify-center">
        {[1, 5, 10, 25].map((num) => (
          <button
            key={num}
            onClick={() => setQuantity(num)}
            disabled={isLoading}
            className={`px-3 py-1 text-sm rounded-md transition-colors
                       ${
                         quantity === num
                           ? 'bg-amber-500 text-white'
                           : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                       }
                       disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {num}
          </button>
        ))}
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Purchase button */}
      <button
        onClick={handlePurchase}
        disabled={isLoading}
        className="w-full py-3 rounded-lg font-medium transition-all duration-200
                   bg-gradient-to-r from-amber-500 to-yellow-500
                   hover:from-amber-400 hover:to-yellow-400
                   text-white shadow-lg shadow-amber-500/20
                   disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.processing', 'Processing...')}
          </>
        ) : (
          <>
            <Ticket className="w-5 h-5" />
            {t('jackpot.buyForTokens', {
              tokens: totalCost.toLocaleString(),
            })}
            <img
              src={publicAsset('token-coin-32.png')}
              alt=""
              className="h-5 w-5"
              draggable={false}
            />
          </>
        )}
      </button>
    </div>
  );
});
